/**
 * Combined API Middleware
 *
 * Composes request management and WebSocket integration middleware.
 * Individual concerns are in focused modules:
 *   - request-queue.ts     — RequestQueueManager, priority types
 *   - websocket-manager.ts — WebSocketManager, connection state, event handling
 *   - request-middleware.ts — dedup, priority, timeout, route-change, retry, logging middleware
 */

import { Middleware } from '@reduxjs/toolkit';
import { Socket } from 'socket.io-client';
import * as React from 'react';
import { config } from '@/config';
import { apiLogger } from '@/core/utils/logger';
import { attachVisibilityChangeListener } from '@/core/utils/windowAttention';
import { SocketClient } from '@/core/socket/SocketClient';

// Re-export types and classes from focused modules
export { RequestPriority, RequestQueueManager } from './request-queue';
export type { RequestMeta } from './request-queue';
export { WebSocketManager } from './websocket-manager';
export type { WebSocketEvent, EventBuffer, ConnectionState } from './websocket-manager';
export {
  requestTimeoutMiddleware,
  routeChangeMiddleware,
  retryMiddleware,
  requestLoggingMiddleware,
} from './request-middleware';

// Import for internal use
import { RequestQueueManager } from './request-queue';
import { WebSocketManager } from './websocket-manager';
import {
  createRequestDeduplicationMiddleware,
  createRequestPriorityMiddleware,
  requestTimeoutMiddleware,
  routeChangeMiddleware,
  retryMiddleware,
  requestLoggingMiddleware,
} from './request-middleware';

// MIDDLEWARE INSTANCES

// Create global instances
export const requestQueue = new RequestQueueManager();
export const websocketManager = new WebSocketManager();

// Create instance-bound middleware
const requestDeduplicationMiddleware = createRequestDeduplicationMiddleware(requestQueue);
const requestPriorityMiddleware = createRequestPriorityMiddleware(requestQueue);

// WEBSOCKET MIDDLEWARE

/**
 * WebSocket middleware for RTK Query integration
 */
export const websocketMiddleware: Middleware = (store) => {
  // Initialize WebSocket manager with store
  websocketManager.initialize(store.dispatch, store);

  return (next) => (action: unknown) => {
    // Handle socket connection actions
    const typedAction = action as { type?: string; payload?: unknown };
    if (typedAction.type === 'socket/connected' && typedAction.payload) {
      // Get the actual socket instance from SocketClient singleton
      const socket = SocketClient.getInstance().getSocket();
      if (socket) {
        websocketManager.connect(socket as Socket);
      }
    }

    if (typedAction.type === 'socket/disconnected') {
      websocketManager.disconnect();
    }

    return next(action);
  };
};

// COMBINED MIDDLEWARE EXPORTS

/**
 * Combined request middleware array
 */
export const requestMiddleware = [
  requestDeduplicationMiddleware,
  requestPriorityMiddleware,
  requestTimeoutMiddleware,
  routeChangeMiddleware,
  retryMiddleware,
  config.features.debugLogging ? requestLoggingMiddleware : null,
].filter(Boolean) as Middleware[];

/**
 * All API middleware (request + websocket)
 */
export const apiMiddleware = [
  ...requestMiddleware,
  websocketMiddleware,
];

// REACT HOOKS

/**
 * React hook for WebSocket connection state
 */
export function useWebSocketState() {
  const [state, setState] = React.useState(() => {
    if (typeof window !== 'undefined' && websocketManager) {
      return websocketManager.getConnectionState();
    }
    return {
      isConnected: false,
      isReconnecting: false,
      missedHeartbeats: 0
    };
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined' && websocketManager) {
      const interval = setInterval(() => {
        const next = websocketManager.getConnectionState();
        setState(prev => {
          if (
            prev.isConnected === next.isConnected &&
            prev.isReconnecting === next.isReconnecting &&
            prev.missedHeartbeats === next.missedHeartbeats
          ) {
            return prev; // Same reference — no re-render
          }
          return next;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, []);

  return state;
}

// WINDOW EVENT LISTENERS

let windowListenersAttached = false;
let detachVisibilityListener: (() => void) | null = null;

function handleVisibilityChange() {
  if (document.hidden) {
    apiLogger.info('[Visibility] Tab hidden, pausing low-priority requests');
  }
}

function handleBeforeUnload() {
  const stats = requestQueue.getStats();
  if (stats.active > 0) {
    apiLogger.info('[Unload] Cancelling all active requests');
  }
}

/**
 * Remove middleware window event listeners.
 * Useful for testing teardown and prevents HMR listener accumulation in dev.
 */
export function cleanupMiddlewareListeners(): void {
  if (typeof window === 'undefined' || !windowListenersAttached) return;
  if (detachVisibilityListener) {
    detachVisibilityListener();
    detachVisibilityListener = null;
  }
  window.removeEventListener('beforeunload', handleBeforeUnload);
  windowListenersAttached = false;
}

if (typeof window !== 'undefined' && !windowListenersAttached) {
  windowListenersAttached = true;
  detachVisibilityListener = attachVisibilityChangeListener({
    onHidden: handleVisibilityChange,
  });
  window.addEventListener('beforeunload', handleBeforeUnload);
}
